import {
  APP_RUN_ID_HEADER,
  ERROR_MESSAGE,
  appRunId,
  type AgoraCredentials,
  type ErrorCode,
  type ResolveLinkResponse,
  type WitnessResponse,
} from '@advaitnetra/shared';

import { API_BASE_URL } from './config/env';
import { INSTANCE_HEADER, noteInstanceHeader } from './monitoring/instanceSource';

/**
 * The only network surface in this bundle.
 *
 * The patient client talks to apps/api and to Agora, and to nothing else — no
 * Supabase, no analytics, no third party (ADR-0007 §3, ADR-0009). Patients are
 * never authenticated: the link token in the URL is the whole capability.
 */

const BASE_URL = API_BASE_URL;

/** Carries a code so the UI can choose copy, and a message safe to display. */
export class ApiFailure extends Error {
  constructor(
    readonly code: ErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'ApiFailure';
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`${BASE_URL}${path}`, {
      ...init,
      headers: {
        'content-type': 'application/json',
        // M0.3: this tab's run, so a crash or a report from the patient side
        // can be tied to the requests it made. Ephemeral — never stored, so
        // it cannot follow a patient across visits (see appRun.ts).
        [APP_RUN_ID_HEADER]: appRunId(),
        ...init?.headers,
      },
    });
  } catch {
    // Network-level failure: offline, DNS, CORS. The patient cannot act on the
    // distinction, so it collapses to one recoverable message.
    throw new ApiFailure('upstream_failure', ERROR_MESSAGE.upstream_failure);
  }

  // INSTANCE-SEPARATION R1.3: every response carries it, success or error —
  // mirrors apps/physio-app/src/api/client.ts's own call.
  noteInstanceHeader(res.headers.get(INSTANCE_HEADER));

  const body: unknown = await res.json().catch(() => null);

  if (!res.ok) {
    const err = body as { code?: ErrorCode; message?: string } | null;
    const code = err?.code ?? 'internal_error';
    // Prefer the server's copy: it is written for the operator and stays in
    // one place. ERROR_MESSAGE is the fallback when the body is unreadable.
    throw new ApiFailure(code, err?.message ?? ERROR_MESSAGE[code] ?? ERROR_MESSAGE.internal_error);
  }

  return body as T;
}

/**
 * Resolve a link WITHOUT minting credentials.
 *
 * Called before the permission prompt, so the page can say "not started yet"
 * or "expired" without first asking an anxious patient for camera access.
 */
export function resolveLink(linkToken: string): Promise<ResolveLinkResponse> {
  return request<ResolveLinkResponse>(`/api/link/${encodeURIComponent(linkToken)}`);
}

/** Exchange the link for RTC credentials. Fails if the session is not live. */
export function joinSession(linkToken: string, uid = 0): Promise<AgoraCredentials> {
  return request<AgoraCredentials>(`/api/link/${encodeURIComponent(linkToken)}/join`, {
    method: 'POST',
    body: JSON.stringify({ uid }),
  });
}

/**
 * SC3 — report that the physio's video track disappeared, once per drop.
 * Fire-and-forget by design: a failed report must never block CallRoom's own
 * countdown UI, so callers swallow the rejection (CallRoom does).
 */
export function reportPhysioWitness(
  linkToken: string,
  occurredAt: string,
): Promise<WitnessResponse> {
  return request<WitnessResponse>(`/api/link/${encodeURIComponent(linkToken)}/witness`, {
    method: 'POST',
    body: JSON.stringify({ occurredAt }),
  });
}

/**
 * Pull the link token out of the URL.
 *
 * Accepts a bare token or a full /join/<token> path, because patients paste
 * links into all sorts of places and some chat clients mangle them.
 */
export function linkTokenFromPath(pathname: string): string | null {
  const match = /\/join\/([A-Za-z0-9_-]{43})\/?$/.exec(pathname);
  if (match?.[1]) return match[1];
  const bare = /^\/?([A-Za-z0-9_-]{43})\/?$/.exec(pathname);
  return bare?.[1] ?? null;
}
